// src/components/hooks/useAccesoEmpresa.ts
//
// Resuelve si la empresa tiene acceso a las pantallas de operativa: hay
// acceso si tiene un plan EFECTIVO (promocional o contratado) o si es
// fundador (su condición no depende de tener plan contratado). Se apoya en
// vista_plan_efectivo_empresa, que ya combina promo + contratado + fundador.
//
// NOTA: esto es solo control de UX. La protección real de los datos debe
// vivir en las RLS de Supabase — un guard de front no impide llamar a la
// API directamente.

import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

interface AccesoEmpresa {
  cargando: boolean;
  tieneAcceso: boolean;
}

export function useAccesoEmpresa(): AccesoEmpresa {
  const [cargando, setCargando] = useState(true);
  const [tieneAcceso, setTieneAcceso] = useState(false);

  useEffect(() => {
    const empresaId = localStorage.getItem('id_empresa');
    if (!empresaId) {
      setCargando(false);
      return;
    }

    let cancelado = false;

    supabase
      .from('vista_plan_efectivo_empresa')
      .select('id_plan_efectivo')
      .eq('id_empresa', empresaId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelado) return;

        if (error) {
          Sentry.captureException(error, { extra: { empresaId } });
          // Ante un error de consulta, no se bloquea el acceso: mejor un
          // falso positivo puntual que dejar fuera a quien sí tiene plan
          // por un fallo de red. La RLS (cuando esté) es la que protege.
          setTieneAcceso(true);
          setCargando(false);
          return;
        }

        // Acceso a la operativa = tener plan EFECTIVO (promo o contratado).
        // Ser fundador (Diamante/Platino) es solo una condición de descuento
        // sobre el plan, no un plan en sí: aunque sea fundador, tiene que
        // elegir/contratar un plan para activar funciones. Sin plan efectivo,
        // no hay acceso — contrata primero (gratis el Diamante, 50% el
        // Platino) y entonces entra.
        const acceso = !!data?.id_plan_efectivo;

        setTieneAcceso(acceso);
        setCargando(false);
      });

    return () => {
      cancelado = true;
    };
  }, []);

  return { cargando, tieneAcceso };
}